import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import CarIcon from '../../../img/car.svg';
import CarAiro from '../../../img/car-airo.svg';
import CarAssociado from '../../../img/car-associado.svg';
import CarAtual from '../../../img/car-atual.svg';
import CarMobi from '../../../img/car-mobi.svg';
import CarPerdaTotal from '../../../img/car-perda-total.svg';
import CarTerceiro from '../../../img/car-terceiro.svg';
import CarExterno from '../../../img/car-externo.svg';
import CarDiretoria from '../../../img/car-diretoria.svg';
import MotoIcon from '../../../img/moto.svg';
import FilterIcon from '../../../img/filter.svg';
import CleanIcon from '../../../img/cross-circle.svg';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import {
  AuditEventType,
  AuditFilters,
  AuditSearchCar,
  AuditTimelineEvent,
} from '../../../interfaces/Audit';
// at-69 (BUG-V63): a Auditoria é só de veículo. O seletor Carro/Peça estava comentado no
// JSX desde a suspensão de Estoque/Compras, mas o `searchMode` continuava travado em
// 'car' e a busca de peças (`useAuditPartsSearch`, sem `enabled`) disparava
// `GET /parts/audit/search` a cada abertura da tela — requisição que nenhum caminho da
// interface podia aproveitar. O modo Peça foi removido; as rotas do backend continuam.
import { useAuditCarsSearch, useCarAuditInfinite } from '../../../infrastructure/react-query/hooks/useAuditQueries';
// at-66 (BUG-V29): a tela deixou de consultar o diretório de colaboradores — a lista de
// autores vem da própria resposta da timeline. Com isso some também o único ponto desta
// tela que podia falhar por conta própria, e o `notify` deixou de ser necessário aqui.
import { mensagemDeErro, notify } from '../../../shared/feedback/notify';
// A ponte cotação → veículo resolve a placa pela busca da auditoria de veículos.
import { searchAuditCars } from '../../../api/auditApi';
import SearchIcon from '../../../img/search-icon.svg';
import PlanningComparisonModal from '../../../components/modal/planning-comparison/PlanningComparisonModal';
import ChecklistEditDetailsModal from '../../../components/modal/checklist-edit-details/ChecklistEditDetailsModal';
import LiberacaoDetailsModal from '../../../components/modal/liberacao-details/LiberacaoDetailsModal';
import SubtarefasDetailsModal from '../../../components/modal/subtarefas-details/SubtarefasDetailsModal';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';

import './AuditoriaPage.css';
// Esqueleto de página compartilhado (`recepcao-conte-do`, `recepcao-header-row`,
// `recepcao-title`). O import é explícito de propósito: depender de CSS carregado por
// outra tela é acoplamento invisível — foi o que originou o BUG-V74 na at-67.
import '../../recepcao/pages/Recepcao.css';
import useUserType from '../../../hooks/useUserType';
import {
  PORTAL_COTACOES_HABILITADO,
  fetchAuditoriaCotacoes,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import { AuditoriaCotacaoItem, COTACAO_STATUS_LABEL } from '../../../interfaces/PortalCotacoes';
import AuditoriaCotacoes from '../components/AuditoriaCotacoes';
import { ORCAMENTO_CILIA_HABILITADO, fetchAuditoriaOrcamentos } from '../../../api/orcamentoCiliaApi';
import { AuditoriaOrcamentoItem } from '../../../interfaces/OrcamentoCilia';
import AuditoriaCilia, { ESTADO_LABEL as ESTADO_ORCAMENTO_LABEL } from '../components/AuditoriaCilia';
import AuditoriaCotacaoOnline from '../components/AuditoriaCotacaoOnline';
// at-74: flag de modo teste dos módulos novos — restringe as abas satélite ao ADMIN.
import { ModuloNovo, moduloEmModoTeste } from '../../../shared/modulos-novos';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema
// at-90: abas configuráveis e a seção de comentários da aba Feedback.
import { AbaDaAuditoria, obterMinhasAbasDeAuditoria } from '../../../api/auditoriaAbasApi';
import ComentariosDoVeiculo from '../../../components/comentarios/ComentariosDoVeiculo';
import { ehFamiliaSaida } from '../../../shared/checklist-familias';
import '../../../shared/feedback/erro-de-tela.css';

/*
 * ⚠️ at-90 (ADR 0042): as constantes `PAPEIS_AUDITORIA_COTACOES` e `PAPEIS_AUDITORIA_CILIA`
 * SAÍRAM. Quem vê qual aba passou a vir da MATRIZ configurável (`GET /auditoria-abas/minhas`),
 * que cobre todas as abas, existentes e futuras — decisão do Victor, 03/09/2026.
 *
 * Para Cotações e Orçamentos a matriz governa só a OFERTA da aba: os dados vivem nos backends
 * dos módulos satélites, cujo `@Roles` continua sendo o teto. O gate por ENV daqueles módulos
 * continua valendo por cima — aba apontando para backend que não existe no ambiente seria pior
 * que não ter a aba.
 */

interface AuditoriaPageProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

const VEHICLES_PER_PAGE = 10;

const getVehicleIcon = (carClass?: string | null) => {
  switch (carClass) {
    case 'Airo Clube':
      return CarAiro;
    case 'Associado':
      return CarAssociado;
    case 'Carros Atual':
      return CarAtual;
    case 'Mobi':
      return CarMobi;
    case 'Perda Total':
      return CarPerdaTotal;
    case 'Terceiro':
      return CarTerceiro;
    case 'Carros Externos':
      return CarExterno;
    case 'Moto':
      return MotoIcon;
    case 'Diretoria':
      return CarDiretoria;
    default:
      return CarIcon;
  }
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  ESTEIRA: 'Esteira',
  COLABORADOR: 'Colaborador',
  PECA: 'Peça',
  APONTAMENTO: 'Apontamento',
  PLANEJAMENTO: 'Planejamento',
  EDICAO_VEICULO: 'Edição de dados',
  FOTO: 'Foto',
  CHECKLIST: 'Checklist',
  LIBERACAO: 'Liberação',
  // at-46: pausar / retomar / concluir o serviço de uma esteira.
  SERVICO: 'Serviço',
  // at-89: sub-tarefas definidas pelo Chefe, ou concluídas/reabertas pelo colaborador.
  SUBTAREFA: 'Sub-tarefa',
  // at-90: comentário do veículo e resposta a comentário.
  COMENTARIO: 'Comentário',
};

/**
 * at-66 (BUG-V86, ex-V17): situação da aprovação de horas em português.
 *
 * O detalhe do apontamento exibia o enum cru — "Aprovação: PENDING" — numa tela que o
 * Chefe, a Administração e o pessoal de Compras/Estoque leem.
 */
const APPROVAL_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovada',
  REJECTED: 'Reprovada',
};

/**
 * at-66 (BUG-V19): duração legível em qualquer escala.
 *
 * Era `Math.round(segundos / 3600)h`, então toda permanência abaixo de 30 minutos
 * aparecia como **"0h"** — indistinguível de "nenhum tempo registrado" — e 90 minutos
 * viravam "2h". Agora a unidade acompanha a grandeza.
 */
function formatDuracao(seconds?: number | null): string | null {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) {
    return null;
  }

  const minutos = Math.floor(total / 60);
  if (minutos < 60) {
    // Abaixo de 1 minuto o valor existe e não pode virar "0 min".
    return minutos < 1 ? 'menos de 1 min' : `${minutos} min`;
  }

  const horas = Math.floor(minutos / 60);
  const minutosRestantes = minutos % 60;
  if (horas < 24) {
    return minutosRestantes > 0 ? `${horas}h ${minutosRestantes}min` : `${horas}h`;
  }

  const dias = Math.floor(horas / 24);
  const horasRestantes = horas % 24;
  return horasRestantes > 0 ? `${dias}d ${horasRestantes}h` : `${dias}d`;
}

const STATUS_COLORS: Record<string, { background: string; color: string }> = {
  CHECKLIST: { background: '#FFE7E7', color: '#8D2D2D' },
  DESMONTAGEM: { background: '#FFE7F7', color: '#9C2F76' },
  ORCAMENTO: { background: '#DCF0FD', color: '#155A82' },
  AGUARDANDO_PECAS: { background: '#C9BBC8', color: '#413740' },
  FUNILARIA: { background: '#FFD6D6', color: '#8C3B3B' },
  PREPARACAO_PINTURA: { background: '#E7FFF6', color: '#17644C' },
  PINTURA: { background: '#E6F7FF', color: '#0E5B73' },
  MECANICA: { background: '#FDE6FF', color: '#7A297F' },
  ELETRICA: { background: '#FFE4CC', color: '#8A4F18' },
  AGUARDANDO_COMPLEMENTOS: { background: '#F0FFE7', color: '#457227' },
  MONTAGEM: { background: '#FFEFE7', color: '#8A5A39' },
  SERVICO_EXTERNO: { background: '#F7E6FF', color: '#6C3391' },
  POLIMENTO: { background: '#E7FFFB', color: '#14665A' },
  HIGIENIZACAO: { background: '#FFEDED', color: '#8A4545' },
  CORRECAO: { background: '#E0F7FF', color: '#155F77' },
  AGUARDANDO_ENTREGA: { background: '#FFEFE0', color: '#8A5A2B' },
  APPROVED: { background: '#E9F9F0', color: '#1E7A4B' },
  PENDING: { background: '#FFF3E7', color: '#A05A1A' },
  REJECTED: { background: '#FDECEC', color: '#A23535' },
  AGUARDANDO: { background: '#F3F4F6', color: '#5B6470' },
  AGUARDANDO_PROXIMA_ETAPA: { background: '#E2E8F0', color: '#475569' },
  RECEBIDO: { background: '#E9F9F0', color: '#1E7A4B' },
  EM_ATRASO: { background: '#FDECEC', color: '#A23535' },
  TROCA: { background: '#FFF3E7', color: '#A05A1A' },
};

const eventTypeOptions: AuditEventType[] = ['ESTEIRA', 'COLABORADOR', 'PECA', 'APONTAMENTO', 'PLANEJAMENTO', 'EDICAO_VEICULO', 'FOTO', 'CHECKLIST', 'LIBERACAO', 'SERVICO', 'SUBTAREFA', 'COMENTARIO'];

/**
 * at-90: os tipos que a aba FEEDBACK oferece no filtro.
 *
 * ⚠️ Isto é UX, não imposição: a curadoria real é do servidor (a rota `/feedback` descarta o
 * resto por whitelist). Oferecer aqui um tipo que a aba nunca mostra faria o usuário filtrar por
 * "Checklist" e concluir que o veículo não tem nenhum.
 */
const eventTypeOptionsFeedback: AuditEventType[] = ['ESTEIRA', 'PLANEJAMENTO', 'SUBTAREFA', 'COMENTARIO'];

function formatDateTime(value?: string | null) {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleDateString('pt-BR');
}

/**
 * at-66 (BUG-V18): data COM hora nos cards da linha do tempo.
 *
 * A timeline é ordenada por instante, mas a maioria dos cards mostrava só a data — três
 * eventos do mesmo dia apareciam empilhados sem nada explicando a ordem. Só as três
 * variantes clicáveis (Planejamento, Liberação, Checklist editado) traziam a hora, o que
 * tornava o conjunto incoerente. O Resumo Atual continua só com a data: ali a informação
 * é "quando o veículo entrou", não sequência de eventos.
 */
function formatDataHoraEvento(value?: string | null) {
  if (!value) {
    return '-';
  }

  const data = new Date(value);
  const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${data.toLocaleDateString('pt-BR')} ${hora}`;
}

function getStatusStyle(status?: string | null, customColor?: string | null) {
  if (customColor) {
    return { background: `${customColor}1a`, color: customColor, border: `1px solid ${customColor}33` };
  }
  return STATUS_COLORS[status || ''] || { background: '#F3F4F6', color: '#374151' };
}

function TimelineIcon({ type }: { type: string }) {
  const label =
    type === 'PECA' ? 'P' :
    type === 'COLABORADOR' ? 'C' :
    type === 'APONTAMENTO' ? 'A' :
    type === 'PLANEJAMENTO' ? 'E' :
    type === 'EDICAO_VEICULO' ? 'V' :
    type === 'FOTO' ? 'F' :
    type === 'CHECKLIST' ? 'K' :
    type === 'LIBERACAO' ? 'L' :
    // at-46: 'S' já é o padrão (ESTEIRA) — o serviço usa 'SV' para não colidir.
    type === 'SERVICO' ? 'SV' :
    // at-89: 'ST' pelo mesmo motivo (não colidir com 'S' nem com 'SV').
    type === 'SUBTAREFA' ? 'ST' :
    // at-90: 'CM' — 'C' já é COLABORADOR e 'CK' seria confundido com checklist ('K').
    type === 'COMENTARIO' ? 'CM' :
    'S';
  return <div className={`auditoria-timeline-icon auditoria-timeline-icon--${type.toLowerCase()}`}>{label}</div>;
}

const AuditoriaPage: React.FC<AuditoriaPageProps> = () => {
  /*
   * Cotação vinda por NAVEGAÇÃO (14/08/2026): a aba "Finalizadas" da Supervisão manda para
   * cá a cotação escolhida via `location.state`. Lido uma vez, no primeiro render, e usado
   * como estado inicial — nunca num efeito que reaplicasse a mesma cotação ao voltar de
   * outra tela pelo histórico do navegador.
   *
   * ⚠️ `state` é dado de fora (o usuário pode forjar): só se aproveita se tiver a forma
   * esperada. Com forma errada, a tela abre normal em vez de quebrar.
   */
  const location = useLocation();
  const userType = useUserType();

  /*
   * at-90: quais abas esta pessoa alcança — vem do servidor, da matriz configurável.
   *
   * ⚠️ `null` = ainda carregando; `[]` = respondeu e não há aba nenhuma liberada. Os dois
   * estados são distintos de propósito: `[]` mostra a mensagem honesta ("nenhuma área liberada
   * para o seu perfil"), e a FALHA da consulta mostra faixa de erro — nunca uma tela sem abas,
   * que seria lida como configuração e não como defeito (regra da at-60).
   */
  const [abasLiberadas, setAbasLiberadas] = useState<AbaDaAuditoria[] | null>(null);
  const [erroAbas, setErroAbas] = useState(false);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const abas = await obterMinhasAbasDeAuditoria();
        if (ativo) {
          setAbasLiberadas(abas);
          setErroAbas(false);
        }
      } catch (error) {
        if (!ativo) return;
        setAbasLiberadas(null);
        setErroAbas(true);
        notify.errorFrom(
          error,
          'Não foi possível verificar quais áreas da Auditoria estão liberadas para você. Recarregue a página; se persistir, acione a TI.',
        );
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  const alcanca = useCallback(
    (chave: string) => (abasLiberadas ?? []).some((aba) => aba.chave === chave),
    [abasLiberadas],
  );

  /*
   * Gate TRIPLO nas abas de módulo satélite, e nenhuma das três camadas é redundante:
   *
   * 1. a MATRIZ (at-90) precisa liberar a aba para o papel;
   * 2. o módulo precisa estar ligado por ENV naquele ambiente — sem ela a aba apontaria para um
   *    backend que não existe, que é o que mantém o Portal invisível em produção;
   * 3. a FLAG de modo teste (at-74) restringe TODA superfície dos módulos novos ao ADMIN
   *    enquanto eles não tiverem go-live próprio.
   *
   * ⚠️ A camada 3 é fácil de perder ao mexer aqui — e perdê-la exporia as abas de Cotações e
   * Orçamentos a COMPRA/ORCAMENTO/CHEFE em produção, onde os módulos estão em modo teste. Há
   * sentinela para ela no fim de `AuditoriaPage.test.tsx`.
   */
  // ⚠️ POR MÓDULO desde 22/09/2026: o Cilia foi liberado e o Portal não, então uma flag só para as
  // duas abas deixou de descrever a realidade. Quem decide é `MODULOS_LIBERADOS`.
  const passaNaFlagDeModuloNovo = (modulo: ModuloNovo) =>
    !moduloEmModoTeste(modulo) || userType === 'ADMIN';

  const podeAuditarVeiculos = alcanca('VEICULOS');
  const podeAuditarFeedback = alcanca('FEEDBACK');
  const podeAuditarCotacoes =
    PORTAL_COTACOES_HABILITADO && alcanca('COTACOES') && passaNaFlagDeModuloNovo('portal-cotacoes');
  const podeAuditarCilia =
    ORCAMENTO_CILIA_HABILITADO && alcanca('ORCAMENTOS') && passaNaFlagDeModuloNovo('orcamento-cilia');
  // Cotação online (20/10/2026): vive no backend de Requisições, então tem os mesmos gates do Cilia.
  const podeAuditarCotacaoOnline =
    ORCAMENTO_CILIA_HABILITADO && alcanca('COTACAO_ONLINE') && passaNaFlagDeModuloNovo('orcamento-cilia');

  /*
   * ⚠️ at-90: NÃO há gate aqui, e a ausência é a correção.
   *
   * Antes esta memo checava `podeAuditarCotacoes`, que era síncrono (derivado do papel no token).
   * Com a matriz, o gate depende de uma consulta — e no PRIMEIRO render ele é sempre `false`.
   * Mantê-lo aqui, numa memo com deps `[]`, fazia a cotação recebida por navegação ser descartada
   * para sempre: o usuário clicava na cotação na Supervisão e caía na aba de veículos, sem nada.
   *
   * Aqui só se PARSEIA o state (dado de fora, pode vir forjado — por isso a checagem de forma).
   * Quem decide se a aba é alcançável é o efeito de escolha de aba, abaixo, quando a matriz chega.
   */
  const cotacaoNavegada = useMemo(() => {
    const bruto = (location.state as { cotacaoParaAuditar?: AuditoriaCotacaoItem } | null)?.cotacaoParaAuditar;
    return bruto && typeof bruto.id === 'string' && typeof bruto.numero === 'string' ? bruto : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * O que está sendo auditado (at-05). Nasce em 'veiculos' sempre — exceto quando a
   * navegação já trouxe uma cotação: aí abrir em veículos faria o usuário clicar de novo
   * naquilo que ele já pediu.
   */
  // Orçamento escolhido no card do veículo, consumido pela aba ao montar (16/08/2026).
  const [orcamentoParaAbrir, setOrcamentoParaAbrir] = useState<AuditoriaOrcamentoItem | null>(null);
  const [modo, setModo] = useState<'veiculos' | 'feedback' | 'cotacoes' | 'cilia' | 'cotacaoOnline'>('veiculos');

  /**
   * at-90: escolhe a aba inicial QUANDO A MATRIZ CHEGA. Duas coisas, na ordem:
   *
   * 1. cotação recebida por navegação (a Supervisão manda para cá a cotação escolhida) abre
   *    direto na aba Cotações — se ela estiver alcançável;
   * 2. quem NÃO tem a aba Veículos (o caso do setor de EVENTOS) cai na primeira que alcança.
   *    Sem isto a tela nasceria na aba de veículos, que ele não pode consultar, e a rota
   *    responderia 403 sozinha.
   *
   * Roda uma vez por chegada da matriz — depois disso, quem troca de aba é o usuário.
   *
   * 🔴 CORRIDA FECHADA POR CONSTRUÇÃO: `escolherAba` marca esta resolução como feita, e a marca
   * vive num **ref**, não em estado.
   *
   * Com `useState` a corrida continuava aberta por CLOSURE OBSOLETA: o efeito captura o valor do
   * render em que a matriz chegou, então um clique ocorrido entre esse render e a execução do
   * efeito era desfeito — a tela voltava sozinha para a aba de veículos, no meio do clique. O ref
   * é lido no momento da execução, então o clique sempre vence.
   *
   * Não é detalhe de teste: a matriz é uma requisição de rede, e em conexão lenta essa janela é
   * de centenas de milissegundos. Apareceu como falha intermitente nesta suíte (5 de 47 testes,
   * variando a cada execução), e foi assim que o defeito foi encontrado.
   */
  const abaInicialResolvida = useRef(false);

  const escolherAba = useCallback((aba: 'veiculos' | 'feedback' | 'cotacoes' | 'cilia' | 'cotacaoOnline') => {
    abaInicialResolvida.current = true;
    setModo(aba);
  }, []);

  useEffect(() => {
    if (!abasLiberadas || abaInicialResolvida.current) return;
    abaInicialResolvida.current = true;

    if (abasLiberadas.length === 0) return;

    if (cotacaoNavegada && podeAuditarCotacoes) {
      setModo('cotacoes');
      return;
    }

    if (podeAuditarVeiculos) setModo('veiculos');
    else if (podeAuditarFeedback) setModo('feedback');
    else if (podeAuditarCotacoes) setModo('cotacoes');
    else if (podeAuditarCilia) setModo('cilia');
    else if (podeAuditarCotacaoOnline) setModo('cotacaoOnline');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abasLiberadas, podeAuditarVeiculos, podeAuditarFeedback, podeAuditarCotacoes, podeAuditarCilia, podeAuditarCotacaoOnline]);
  /*
   * Cotação a abrir JÁ SELECIONADA ao trocar para a aba Cotações (14/08/2026, pedido do
   * Victor: a cotação é movimentação do veículo, e a auditoria dele precisa levar até ela).
   * O detalhe continua morando na aba Cotações — daqui só se navega, nunca se duplica.
   */
  const [cotacaoParaAbrir, setCotacaoParaAbrir] = useState<AuditoriaCotacaoItem | null>(cotacaoNavegada);

  const [selectedId, setSelectedId] = useState('');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isTimelineHelpOpen, setIsTimelineHelpOpen] = useState(false);
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const [vehicleFilterInput, setVehicleFilterInput] = useState('');
  const [vehiclePage, setVehiclePage] = useState(1);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [filters, setFilters] = useState<AuditFilters>({
    startDate: '',
    endDate: '',
    collaboratorId: '',
    eventType: '',
  });
  const [tempFilters, setTempFilters] = useState<AuditFilters>({
    startDate: '',
    endDate: '',
    collaboratorId: '',
    eventType: '',
  });

  const [isPlanningModalOpen, setIsPlanningModalOpen] = useState(false);
  const [selectedPlanningEvent, setSelectedPlanningEvent] = useState<AuditTimelineEvent | null>(null);

  const handleOpenPlanningModal = (event: AuditTimelineEvent) => {
    setSelectedPlanningEvent(event);
    setIsPlanningModalOpen(true);
  };

  const [isChecklistEditModalOpen, setIsChecklistEditModalOpen] = useState(false);
  const [selectedChecklistEditEvent, setSelectedChecklistEditEvent] = useState<AuditTimelineEvent | null>(null);

  const handleOpenChecklistEditModal = (event: AuditTimelineEvent) => {
    setSelectedChecklistEditEvent(event);
    setIsChecklistEditModalOpen(true);
  };

  const [isLiberacaoModalOpen, setIsLiberacaoModalOpen] = useState(false);
  const [selectedLiberacaoEvent, setSelectedLiberacaoEvent] = useState<AuditTimelineEvent | null>(null);

  const handleOpenLiberacaoModal = (event: AuditTimelineEvent) => {
    setSelectedLiberacaoEvent(event);
    setIsLiberacaoModalOpen(true);
  };

  // at-89: só o evento do CHEFE (definir sub-tarefas) tem detalhe a abrir — ver o comentário
  // no `SubtarefasDetailsModal`.
  const [isSubtarefasModalOpen, setIsSubtarefasModalOpen] = useState(false);
  const [selectedSubtarefasEvent, setSelectedSubtarefasEvent] = useState<AuditTimelineEvent | null>(null);

  const handleOpenSubtarefasModal = (event: AuditTimelineEvent) => {
    setSelectedSubtarefasEvent(event);
    setIsSubtarefasModalOpen(true);
  };

  /*
   * Conta TODOS os filtros, autor incluído (at-05, 14/08/2026). Antes o autor ficava de
   * fora porque tinha botão próprio, e a hint precisava explicar a exceção — agora ele é
   * um campo do mesmo modal, e ficar de fora do contador é que seria inexplicável.
   */
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.startDate) count++;
    if (filters.endDate) count++;
    if (filters.eventType) count++;
    if (filters.collaboratorId) count++;
    return count;
  }, [filters]);

  const carsSearchQuery = useAuditCarsSearch(vehicleFilterInput.trim(), vehiclePage, VEHICLES_PER_PAGE);
  // at-90: na aba Feedback a consulta é de OUTRA rota (curadoria e resumo reduzido no servidor).
  const emFeedback = modo === 'feedback';
  const carAuditQuery = useCarAuditInfinite(selectedId, filters, emFeedback);

  // Timeline paginada (at-28): as páginas acumuladas do useInfiniteQuery viram a
  // lista da linha do tempo; o resumo do carro vem da primeira página.
  const carAuditPages = carAuditQuery.data?.pages;
  const carAuditFirstPage = carAuditPages?.[0];

  /*
   * Cotações de peças da PLACA selecionada (at-05, 14/08/2026): a cotação é movimentação
   * do veículo, e a auditoria dele precisa apontá-la. A chave é a placa — é ela que a
   * cotação carrega —, então a lista cobre todas as passagens do veículo, não só este
   * atendimento. `null` = consulta falhou (estado distinto de "sem cotações": afirmar
   * "nenhuma" a partir de um erro é a classe de defeito do at-60).
   *
   * Só consulta quem alcança o Portal (`podeAuditarCotacoes`) — para os demais papéis a
   * seção não existe, em vez de existir e devolver 403.
   */
  const [cotacoesDaPlaca, setCotacoesDaPlaca] = useState<AuditoriaCotacaoItem[] | null>([]);
  const placaSelecionada = carAuditFirstPage?.car?.plate ?? null;
  useEffect(() => {
    if (!podeAuditarCotacoes || !placaSelecionada) {
      setCotacoesDaPlaca([]);
      return;
    }
    let ativo = true;
    (async () => {
      try {
        const resposta = await fetchAuditoriaCotacoes(placaSelecionada, 1, 20);
        if (ativo) setCotacoesDaPlaca(resposta.data);
      } catch {
        // O sinal visível é a linha "não foi possível verificar" no card — o silêncio
        // aqui é só do toast, para a troca de veículo não empilhar avisos repetidos.
        if (ativo) setCotacoesDaPlaca(null);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [podeAuditarCotacoes, placaSelecionada]);

  /*
   * ORÇAMENTOS da placa (16/08/2026, pedido do Victor) — irmão da seção de cotações acima.
   *
   * O orçamento é o começo da história das peças deste veículo: é dele que saem a triagem
   * por esteira e a solicitação de compra. Quem abre a auditoria de um veículo e vê só as
   * cotações fica sem o elo anterior, que é onde as decisões por peça acontecem.
   *
   * Mesmos três estados da seção de cotações: `null` = falha (a tela diz que não conseguiu
   * verificar), `[]` = nenhum registrado, lista = os orçamentos da placa.
   */
  const [orcamentosDaPlaca, setOrcamentosDaPlaca] = useState<AuditoriaOrcamentoItem[] | null>([]);
  useEffect(() => {
    if (!podeAuditarCilia || !placaSelecionada) {
      setOrcamentosDaPlaca([]);
      return;
    }
    let ativo = true;
    (async () => {
      try {
        const resposta = await fetchAuditoriaOrcamentos(placaSelecionada, 1, 20);
        if (ativo) setOrcamentosDaPlaca(resposta.data);
      } catch {
        // Silêncio só do toast (a troca de veículo empilharia avisos); o sinal visível é a
        // linha "não foi possível verificar" no próprio card.
        if (ativo) setOrcamentosDaPlaca(null);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [podeAuditarCilia, placaSelecionada]);

  /**
   * at-66 (BUG-V29): a lista de pessoas do filtro vem dos PRÓPRIOS EVENTOS do veículo.
   *
   * Antes vinha do diretório de colaboradores (`GET /users/collaborators`), que lista só
   * `userType = COLABORADOR` — e a maior parte dos eventos da Auditoria é executada por
   * Chefe, Recepção ou Administração. Esses autores nunca apareciam no seletor e não
   * podiam ser filtrados, o que sugeria que a auditoria não registra o que os gestores
   * fazem. O backend passou a devolver `autores` junto da timeline (união de todos os
   * eventos do veículo, antes dos filtros), então a tela não faz requisição nenhuma aqui.
   *
   * ⚠️ at-05 (14/08/2026, decisão do Victor): o autor deixou de ter BOTÃO E MODAL PRÓPRIOS
   * e virou um campo dentro de "Filtros" — é onde ele faz sentido, junto dos outros
   * critérios da timeline. Saíram com o modal a busca, a paginação e os três estados que
   * só existiam para ele (`collaboratorSearchInput`, `collaboratorPage`,
   * `isCollaboratorModalOpen`) — removidos, não comentados (regra de 02/08/2026).
   */
  const autores = useMemo(() => carAuditFirstPage?.autores ?? [], [carAuditFirstPage?.autores]);

  const options = useMemo<AuditSearchCar[]>(
    () => carsSearchQuery.data?.cars || [],
    [carsSearchQuery.data?.cars],
  );

  const vehicleOptions: AuditSearchCar[] = carsSearchQuery.data?.cars || [];
  const totalVehiclePages = Math.max(1, Math.ceil((carsSearchQuery.data?.total || 0) / VEHICLES_PER_PAGE));

  const activeAudit = carAuditFirstPage;
  const loading = carsSearchQuery.isLoading || carAuditQuery.isLoading;

  // at-64 (BUG-V21): a timeline vinha do TanStack Query e ninguém lia o `isError` dela.
  // Falha de permissão ou de servidor deixava a tela IDÊNTICA a "nenhum veículo
  // selecionado" — o usuário concluía que o veículo não tinha histórico. Auditoria é
  // justamente a tela onde "não há registro" é uma afirmação séria.
  const erroTimeline =
    selectedId && carAuditQuery.isError
      ? mensagemDeErro(
          carAuditQuery.error,
          'Não foi possível carregar a linha do tempo deste registro. Recarregue a página; se persistir, acione a TI.',
        )
      : null;

  // No auto-select first item by default, letting it start as empty

  useEffect(() => {
    if (vehiclePage > totalVehiclePages) {
      setVehiclePage(totalVehiclePages);
    }
  }, [totalVehiclePages, vehiclePage]);

  const selectedLabel = useMemo(() => {
    const selected = options.find((item) => item.id === selectedId);
    if (selected) {
      return `${selected.plate} • ${selected.model}`;
    }

    if (carAuditFirstPage?.car) {
      return `${carAuditFirstPage.car.plate} • ${carAuditFirstPage.car.model}`;
    }

    return '';
  }, [carAuditFirstPage?.car, options, selectedId]);

  const events = useMemo<AuditTimelineEvent[]>(
    // O descarte dos eventos "activity-*" passou para o backend (at-28) — aqui
    // só se acumulam as páginas carregadas.
    () => carAuditPages?.flatMap((page) => page.events) || [],
    [carAuditPages],
  );

  const totalEvents = carAuditPages?.[carAuditPages.length - 1]?.totalEvents ?? 0;

  const handleClear = () => {
    setSelectedId('');
    setVehicleFilterInput('');
    setVehiclePage(1);
    setIsVehicleModalOpen(false);
    setFilters({ startDate: '', endDate: '', collaboratorId: '', eventType: '' });
    setTempFilters({ startDate: '', endDate: '', collaboratorId: '', eventType: '' });
  };

  const handleOpenFiltersModal = () => {
    setTempFilters(filters);
    setIsFiltersModalOpen(true);
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setIsFiltersModalOpen(false);
  };

  const handleOpenVehicleModal = () => {
    setVehicleFilterInput('');
    setVehiclePage(1);
    setIsVehicleModalOpen(true);
  };

  const handleSelectVehicle = (carId: string) => {
    setSelectedId(carId);
    setIsVehicleModalOpen(false);
  };

  /*
   * Ponte INVERSA cotação → veículo (14/08/2026, pedido do Victor): a placa no resumo da
   * cotação leva à auditoria do veículo. A cotação só conhece a PLACA; a auditoria de
   * veículos seleciona por atendimento — a resolução usa a busca existente, que ordena por
   * `updatedAt desc`, então o primeiro match exato é o atendimento MAIS RECENTE da placa
   * (o alvo natural quando o veículo re-entrou na oficina).
   */
  const abrirVeiculoDaPlaca = async (placa: string) => {
    try {
      const resposta = await searchAuditCars(placa, 1, 10);
      // Match EXATO de placa: a busca é `contains`, e "ABC1" acharia "ABC1D23" e "TABC12".
      // O `?? []` blinda contra resposta com shape inesperado — sem ele, o TypeError cairia
      // no catch e o aviso mentiria o motivo.
      const alvo = (resposta.cars ?? []).find((car) => car.plate === placa);
      if (!alvo) {
        notify.error(
          `Nenhum veículo com a placa ${placa} foi encontrado na auditoria de veículos.`,
        );
        return;
      }
      setSelectedId(alvo.id);
      escolherAba('veiculos');
    } catch {
      /*
       * Texto FIXO, não `errorFrom` (regra da at-65): num 500 o extrator devolve "Erro
       * interno do servidor", que não diz o que o clique deixou de fazer. Aqui a
       * consequência importa mais que o motivo. E sem troca de aba em falha: navegar para
       * a aba de veículos SEM o veículo carregado pareceria "esta placa não tem histórico".
       */
      notify.error('Não foi possível localizar o veículo desta placa. Tente novamente; se persistir, acione a TI.');
    }
  };


  const renderEventDetail = (event: AuditTimelineEvent) => {
    if (event.eventType === 'PECA') {
      return event.metadata?.reason || event.metadata?.payload?.field || 'Sem detalhe adicional';
    }

    if (event.eventType === 'APONTAMENTO') {
      const sub = event.metadata?.subType;
      const rotuloAprovacao = (valor?: string | null) =>
        valor ? APPROVAL_STATUS_LABELS[valor] || valor : null;

      if (sub === 'PAUSE') return `Motivo: ${event.metadata?.reason || '-'}`;
      if (sub === 'END') {
        const partes: string[] = [];
        const duracao = formatDuracao(event.metadata?.durationInSeconds);
        if (duracao) partes.push(`Duração total: ${duracao}`);
        const pausado = formatDuracao(event.metadata?.totalPausedSeconds);
        if (pausado) partes.push(`Pausado: ${pausado}`);
        const aprovacao = rotuloAprovacao(event.metadata?.approvalStatus);
        if (aprovacao) partes.push(`Aprovação: ${aprovacao}`);
        // at-66 (BUG-V14): o motivo da reprovação já vinha no evento e nunca era exibido —
        // a timeline dizia "Aprovação: Reprovada" sem dizer por quê, que é justamente o
        // que o Chefe escreveu para o colaborador ler.
        if (event.metadata?.rejectionReason) partes.push(`Motivo da reprovação: ${event.metadata.rejectionReason}`);
        return partes.length > 0 ? partes.join(' • ') : 'Finalizado';
      }
      const aprovacao = rotuloAprovacao(event.metadata?.approvalStatus);
      return aprovacao ? `Aprovação: ${aprovacao}` : 'Apontamento operacional';
    }

    if (event.eventType === 'ESTEIRA') {
      const tempo = formatDuracao(event.metadata?.secondsInStatus);
      return tempo ? `Tempo registrado: ${tempo}` : 'Movimentação de esteira';
    }

    // at-46: pausar / retomar / concluir o serviço de uma esteira.
    if (event.eventType === 'SERVICO') {
      const partes: string[] = [];
      if (event.metadata?.esteiraNome) partes.push(`Esteira: ${event.metadata.esteiraNome}`);
      if (event.metadata?.origemLabel) partes.push(`Registrado ${event.metadata.origemLabel}`);
      if (event.metadata?.observacao) partes.push(`Motivo: ${event.metadata.observacao}`);
      return partes.length > 0 ? partes.join(' • ') : 'Mudança de estado do serviço';
    }

    /* at-89: a marcação do colaborador. O `SET_SUBTAREFAS` não passa por aqui — ele tem card
       próprio com modal, e repetir a descrição no "Detalhe:" só duplicaria a mesma frase. */
    if (event.eventType === 'SUBTAREFA') {
      const partes: string[] = [];
      if (event.metadata?.subtarefa) partes.push(`Sub-tarefa: ${event.metadata.subtarefa}`);
      if (event.metadata?.esteiraNome) partes.push(`Esteira: ${event.metadata.esteiraNome}`);
      return partes.length > 0 ? partes.join(' • ') : 'Registro de sub-tarefa';
    }

    /* at-90: o texto do comentário JÁ é a `description` do card. Aqui vai o contexto — a que
       comentário a resposta responde, e se o comentário veio de outra passagem do veículo. */
    if (event.eventType === 'COMENTARIO') {
      const partes: string[] = [];
      if (event.metadata?.ehResposta && event.metadata?.respostaAoTexto) {
        const original = String(event.metadata.respostaAoTexto);
        const trecho = original.length > 80 ? `${original.slice(0, 80)}...` : original;
        partes.push(
          `Em resposta a ${event.metadata.respostaAoAutor ?? 'um comentário'}: "${trecho}"`,
        );
      }
      if (event.metadata?.deOutroAtendimento) {
        partes.push('Registrado em uma passagem anterior deste veículo');
      }
      return partes.length > 0 ? partes.join(' • ') : 'Comentário interno da equipe';
    }

    if (event.eventType === 'COLABORADOR') {
      const tempoVinculo = formatDuracao(event.metadata?.secondsResponsible);
      // at-66 (BUG-V13): na remoção, o motivo já vem resolvido pelo backend e é a
      // informação principal do card — antes o detalhe caía no genérico "Evento registrado".
      if (event.metadata?.motivoLabel) {
        return tempoVinculo
          ? `${event.metadata.motivoLabel} • Tempo vinculado: ${tempoVinculo}`
          : String(event.metadata.motivoLabel);
      }
      if (event.metadata?.performedByName) {
        return `Executado por: ${event.metadata.performedByName}${tempoVinculo ? ` • Tempo: ${tempoVinculo}` : ''}`;
      }
      return tempoVinculo ? `Tempo responsável: ${tempoVinculo}` : 'Atribuição de colaborador';
    }

    if (event.eventType === 'EDICAO_VEICULO') {
      const { fieldLabel, oldValue, newValue } = event.metadata || {};
      if (!fieldLabel) {
        return null;
      }
      return `${fieldLabel || 'Campo'}: "${oldValue ?? '-'}" → "${newValue ?? '-'}"`;
    }

    if (event.eventType === 'FOTO') {
      const acao = event.metadata?.action === 'DELETE_PHOTO' ? 'excluída' : 'enviada';
      return `Foto ${acao}${event.metadata?.filename ? `: ${event.metadata.filename}` : ''}`;
    }

    if (event.eventType === 'CHECKLIST') {
      const tipoLabel = ehFamiliaSaida(event.metadata?.type) ? 'Saída' : 'Entrada';
      const acao = event.metadata?.action === 'COMPLETE_CHECKLIST' ? 'concluído' : 'iniciado';
      return `Checklist de ${tipoLabel} ${acao}`;
    }

    return event.description || 'Evento registrado';
  };

  const isScreenLoading = loading && !activeAudit;

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela mostra',
      conteudo: (
        <p>
          A Auditoria é a trilha completa de um atendimento: tudo o que aconteceu com o veículo desde a entrada na
          oficina — mudanças de esteira, colaboradores atribuídos e removidos, horas trabalhadas, fotos,
          checklists, liberações administrativas e edições de dados. Cada registro guarda <strong>quem fez</strong>{' '}
          e <strong>quando</strong>. Os registros são gravados automaticamente pelo sistema no momento de cada ação
          e <strong>não podem ser alterados nem apagados</strong> por esta tela — aqui é somente consulta.
        </p>
      ),
    },
    ...(podeAuditarFeedback
      ? [
          {
            titulo: 'A aba Feedback',
            conteudo: (
              <div>
                <p>
                  A aba <strong>Feedback</strong> mostra só o que responde{' '}
                  <strong>"em que pé está este carro"</strong>: os{' '}
                  <strong>comentários</strong> da equipe, o <strong>planejamento de etapas</strong>,
                  as <strong>sub-tarefas</strong> (as que foram planejadas e as que já foram
                  concluídas, com quem concluiu) e as <strong>movimentações entre etapas</strong>.
                  Checklist, horas trabalhadas, peças e edições de dados <strong>não aparecem</strong>{' '}
                  aqui — são da aba Veículos.
                </p>
                <p>
                  Ela existe para quem precisa dar retorno a um associado ou a um colega sem
                  precisar ligar para a oficina. O recorte é feito pelo próprio sistema: não há
                  como ver os outros eventos por esta aba.
                </p>
                <p>
                  ⚠️ É a única parte desta tela em que se <strong>escreve</strong>: quem tem
                  permissão pode registrar comentários e responder aos existentes, no quadro{' '}
                  <strong>Resumo Atual</strong>. Um comentário registrado{' '}
                  <strong>não pode ser editado nem apagado</strong> por ninguém — para corrigir,
                  registre um comentário novo. Quem pode escrever é definido na Administração do
                  Sistema; se você não vê o campo, o seu perfil não recebeu essa permissão.
                </p>
                <p>
                  Comentários marcados como <strong>Atendimento anterior</strong> vieram de uma
                  passagem <strong>anterior</strong> deste veículo pela oficina — eles acompanham a
                  placa, não a entrada atual.
                </p>
              </div>
            ),
          },
        ]
      : []),
    ...(podeAuditarCotacoes
      ? [
          {
            titulo: 'Veículos e Cotações: o que escolher',
            conteudo: (
              <div>
                <p>
                  Esta tela audita <strong>duas coisas diferentes</strong>, e as abas no topo escolhem qual.{' '}
                  <strong>Veículos</strong> traz a trilha de um atendimento na oficina. <strong>Cotações</strong>{' '}
                  traz a trilha de uma compra: o que a equipe fez e o que cada fornecedor fez.
                </p>
                <p>
                  As duas se ligam <strong>nos dois sentidos</strong>: no <strong>Resumo Atual</strong> de um
                  veículo aparecem as <strong>cotações de peças da placa</strong>, com o clique levando à
                  auditoria daquela cotação; e no resumo de uma cotação a <strong>placa</strong> é clicável e
                  leva de volta à auditoria do veículo — sempre ao atendimento mais recente daquela placa. A
                  lista de cotações é por placa, então cobre todas as passagens do veículo pela oficina.
                </p>
                <p>
                  A aba <strong>Cotações</strong> só aparece para quem participa desse processo. Se você não a vê,
                  é porque o seu perfil não alcança as cotações — não é falha da tela.
                </p>
              </div>
            ),
          },
          {
            titulo: 'A linha do tempo de uma cotação',
            conteudo: (
              <div>
                <p>
                  Cada evento vem marcado com a sua origem: <strong>Equipe</strong> (o que comprador e gestor
                  fizeram — criar, enviar convites, escolher fornecedor, fechar a cesta, aprovar ou reprovar item,
                  gerar pedidos), <strong>Fornecedor</strong> (abriu o link, visualizou, enviou ou reenviou
                  proposta, declinou, ou tentou entrar com um link já vencido) e <strong>Sistema</strong> (o envio
                  de cada convite, por e-mail ou WhatsApp, com a falha quando houve).
                </p>
                <p>
                  É a origem <strong>Fornecedor</strong> que responde à dúvida mais comum numa disputa: se o
                  convite chegou, se foi aberto e quando a proposta entrou. Um envio por WhatsApp aparece como{' '}
                  <strong>link montado para envio manual</strong> — quem envia é o comprador, do próprio aparelho,
                  então o sistema não tem como afirmar que a mensagem chegou.
                </p>
                <p>
                  Registros anteriores a agosto de 2026 podem mostrar um código no lugar do nome de quem agiu: o
                  nome passou a ser guardado junto do registro só a partir dessa data, e não há como recuperá-lo
                  para trás.
                </p>
              </div>
            ),
          },
        ]
      : []),
    ...(podeAuditarCotacaoOnline
      ? [
          {
            titulo: 'A aba Cotação online',
            conteudo: (
              <div>
                <p>
                  A aba <strong>Cotação online</strong> lista toda pesquisa de preço feita nas requisições:
                  quem pediu, a peça, a placa, quando, como (cotação da requisição inteira, pelo botão Cotar peças online, ou rebusca de uma peça)
                  e o desfecho. Clicando numa linha, aparece o que cada canal trouxe, com os links dos
                  anúncios. Na pesquisa automática, quem aparece é quem concluiu a requisição. A mesma
                  pesquisa também aparece na trilha da requisição, na aba <strong>Orçamentos</strong>.
                </p>
              </div>
            ),
          },
        ]
      : []),
    ...(podeAuditarCilia
      ? [
          {
            titulo: 'A aba Orçamentos',
            conteudo: (
              <div>
                <p>
                  A aba <strong>Orçamentos</strong> traz a trilha de um orçamento do Cilia: a importação do XML,
                  o que a conferência decidiu peça por peça, o que foi criado ou atualizado no Nasajon, a
                  solicitação de compra e o desfecho dela. Se o orçamento tem complementos, <strong>todas as
                  versões aparecem juntas</strong> — a história é do orçamento inteiro, não de cada XML.
                </p>
                <p>
                  Ela também mostra <strong>o que o sistema decidiu sozinho</strong>: esteira preenchida pelo
                  aprendizado de importações anteriores, peça reclassificada na importação de um complemento,
                  avisos automáticos. Esses eventos aparecem com o autor <strong>Sistema</strong>.
                </p>
                <p>
                  A aba só aparece para quem participa desse processo. Se você não a vê, é porque o seu perfil
                  não alcança os orçamentos — não é falha da tela.
                </p>
              </div>
            ),
          },
        ]
      : []),
    {
      titulo: 'Como consultar',
      conteudo: (
        <>
          <p>1. Clique em <strong>Veículos</strong> e busque por placa, modelo ou identificador — a auditoria é sempre de um veículo por vez.</p>
          <p>
            2. Em <strong>Filtros</strong>, refine a linha do tempo por período, tipo de evento e{' '}
            <strong>autor</strong>. Os critérios são aplicados juntos, ao confirmar.
          </p>
          <p>3. <strong>Limpar</strong> desfaz a seleção e todos os filtros.</p>
          <p>
            A lista de <strong>autores</strong> do filtro mostra <strong>quem aparece na linha do tempo do
            veículo escolhido</strong> — de qualquer papel, não só mecânicos: quem cadastrou o veículo, quem
            moveu de esteira, quem liberou, quem aprovou horas. Por isso ela só tem conteúdo depois que um
            veículo é selecionado, e nunca traz alguém que não tenha feito nada neste atendimento.
          </p>
          <p>
            O número ao lado de <strong>Filtros</strong> conta quantos critérios estão valendo.
          </p>
        </>
      ),
    },
    {
      titulo: 'Resumo Atual',
      conteudo: (
        <p>
          O painel <strong>Resumo Atual</strong> mostra a situação do veículo <strong>agora</strong> — número do
          evento, data de entrada, colaboradores vinculados, aprovação, chassi e a esteira em que ele está. É o
          estado presente, não o histórico: para ver como o veículo chegou até aqui, use a{' '}
          <strong>Linha do Tempo</strong> ao lado.
        </p>
      ),
    },
    {
      titulo: 'Linha do Tempo',
      conteudo: (
        <p>
          Os acontecimentos aparecem do mais recente para o mais antigo, carregados aos poucos — use{' '}
          <strong>Carregar mais eventos</strong> para ver o restante. Cada tipo de acontecimento tem uma cor
          própria, e alguns têm um detalhamento completo que abre ao clicar no título. A explicação de cada tipo
          está no <strong>?</strong> no cabeçalho do quadro <strong>Linha do Tempo</strong>.
        </p>
      ),
    },
    {
      titulo: 'Quem usa esta tela',
      conteudo: (
        <div>
          <p>
            <strong>Quais abas cada perfil enxerga é configurado na Administração do Sistema</strong>{' '}
            (em Oficina, na aba "Auditoria e Comentários"). Se você não vê uma aba que um colega vê,
            não é falha da tela: o seu perfil não foi incluído nela.
          </p>
          <p>
            A tela é <strong>somente leitura</strong>, com uma exceção: os{' '}
            <strong>comentários</strong> da aba Feedback, que podem ser registrados e respondidos
            por quem tiver permissão. Nenhuma outra ação operacional é feita por aqui.
          </p>
          <p>
            Registros sem um usuário identificado aparecem como <strong>Sistema</strong>: são ações
            automáticas, como encerramentos feitos pelo próprio sistema.
          </p>
        </div>
      ),
    },
  ];

  const timelineHelpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'Os tipos de evento',
      conteudo: (
        <>
          <p><strong>Esteira</strong> — movimentações do veículo entre etapas, com o tempo registrado na etapa anterior.</p>
          <p>
            <strong>Colaborador</strong> — atribuições e remoções de colaboradores no veículo, com o motivo da
            saída: entrega do veículo, cadastro desativado, esteira de destino que não recebe colaborador,
            regra de especialidade ou remoção feita pelo Chefe de Oficina.
          </p>
          <p><strong>Apontamento</strong> — as sessões de trabalho: início, pausas (com o motivo), finalização (com duração e situação da aprovação das horas).</p>
          <p><strong>Serviço</strong> — o desfecho do serviço de cada esteira: concluído, deixado inacabado (com o motivo da parada escolhido na lista e o detalhe, quando informados) ou retomado.</p>
          <p><strong>Checklist</strong> — início e conclusão dos checklists de entrada e saída, e as edições feitas depois de concluídos.</p>
          <p><strong>Liberação</strong> — as liberações administrativas concedidas ou removidas.</p>
          <p><strong>Planejamento</strong> — alterações no planejamento de esteiras do veículo.</p>
          <p><strong>Edição de dados</strong> — alterações nos dados cadastrais do atendimento, com o valor anterior e o novo.</p>
          <p><strong>Foto</strong> — envio e exclusão de fotos do veículo.</p>
          <p><strong>Peça</strong> — alterações em peças vinculadas ao atendimento.</p>
          <p>
            <strong>Comentário</strong> — o que a equipe registrou sobre o veículo, e as respostas
            a esses registros. Comentários são <strong>internos</strong>: o associado não tem acesso
            a eles, e eles não saem no PDF do checklist. Um comentário marcado como de{' '}
            <strong>passagem anterior</strong> foi registrado numa entrada anterior desta placa na
            oficina.
          </p>
        </>
      ),
    },
    {
      titulo: 'Eventos com detalhamento completo',
      conteudo: (
        <>
          <p>Três tipos guardam um antes/depois completo, aberto pelo clique no título do evento:</p>
          <p>
            <strong>Planejamento de Esteira</strong> — compara o planejamento anterior com o novo, marcando
            etapas adicionadas e removidas. Etapas que foram tiradas da seleção mas <strong>continuaram no
            planejamento</strong> por terem serviço em andamento ou inacabado aparecem em destaque próprio: elas
            só saem depois que o serviço for concluído.
          </p>
          <p>
            <strong>Liberação Administrativa</strong> — mostra o que foi concedido ou removido (liberação total e
            por esteira), a observação registrada e, quando uma remoção encerrou atividades em andamento, quais
            foram — as horas trabalhadas são preservadas e vão para a aprovação do Chefe de Oficina.
          </p>
          <p>
            <strong>Checklist editado</strong> — mostra cada item alterado (situação e observação, antes e depois),
            as observações gerais alteradas e as mídias adicionadas e excluídas, com miniatura.
          </p>
          <p>Registros muito antigos podem não ter esse detalhamento — nesse caso o modal avisa que se trata de uma alteração antiga.</p>
        </>
      ),
    },
  ];

  return (
    <div
      className="Frame1000004012 auditoria-page-shell"
      style={{
        height: isScreenLoading ? '100vh' : 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: isScreenLoading ? 'center' : 'space-between',
        alignItems: isScreenLoading ? 'center' : 'flex-start',
        backgroundColor: isScreenLoading ? '#808080' : 'transparent',
      }}
    >
      {isScreenLoading && <FullScreenLoader />}

      {/*
        Esqueleto COMPARTILHADO de página (13/08/2026): `recepcao-conte-do` e
        `recepcao-header-row` substituem os paddings e o flex inline que replicavam à mão os
        mesmos valores das demais telas. Além da coerência de título e margens, a tela passa a
        herdar a responsividade completa do Recepcao.css — o degrau de 1024px e o espaço
        reservado ao menu mobile, que a media query própria daqui não previa.
      */}
      <div className="ConteDo recepcao-conte-do">
        <div className="recepcao-header-row">
          <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Auditoria
            <button
              type="button"
              className="help-circle-btn"
              title="Como Funciona"
              onClick={() => setIsHelpOpen(true)}
            >
              ?
            </button>
          </div>
        </div>

        {/*
          Seletor do que auditar (at-05, 14/08/2026). A aba Cotações só existe quando o
          módulo do Portal está ligado por env E o papel tem acesso — sem os dois, a tela
          fica exatamente como antes, sem aba nenhuma. É o mesmo gate que mantém o Portal
          invisível em produção; aba morta apontando para um backend inexistente seria pior
          que não ter a funcionalidade.
        */}
        {/*
          at-90: falha ao descobrir as abas é DITA. Sem isto, a tela ficaria sem aba nenhuma e
          o usuário leria isso como "meu perfil não tem acesso" em vez de "a consulta falhou".
        */}
        {erroAbas && (
          <p className="erro-de-tela" role="alert">
            Não foi possível verificar quais áreas da Auditoria estão liberadas para o seu perfil.
            Recarregue a página; se o problema persistir, acione a TI.
          </p>
        )}

        {/*
          at-90: nenhuma aba liberada é um estado LEGÍTIMO — é como o setor de EVENTOS vê a tela
          antes de o ADMIN conceder a aba Feedback (bootstrap inerte). A mensagem diz o que é, em
          vez de deixar uma página em branco.
        */}
        {!erroAbas && abasLiberadas?.length === 0 && (
          <div className="auditoria-empty-state">
            Nenhuma área da Auditoria está liberada para o seu perfil. Quem define isso é a
            Administração do Sistema — procure um administrador se você precisa consultar esta tela.
          </div>
        )}

        {(podeAuditarFeedback || podeAuditarCotacoes || podeAuditarCilia || podeAuditarCotacaoOnline) && (
          /*
            `role="tablist"` não é enfeite: o botão que abre o seletor de veículo tem o
            texto "Veículos" e a aba também — sem o papel, uma busca por texto na tela
            encontra os dois, e foi assim que a aba nova derrubou 5 testes desta página.
            Com o papel, cada um é alcançável sem ambiguidade (e leitor de tela também os
            distingue).

            ⚠️ Cada aba tem o SEU gate — desde a at-90, vindo da matriz configurável. Por isso o
            wrapper aparece com qualquer aba além de Veículos, e cada botão é renderizado à parte.
          */
          <div className="recepcao-tabs" role="tablist" aria-label="O que auditar">
            {podeAuditarVeiculos && (
              <button
                type="button"
                role="tab"
                aria-selected={modo === 'veiculos'}
                className={`recepcao-tab ${modo === 'veiculos' ? 'active' : ''}`}
                onClick={() => escolherAba('veiculos')}
              >
                Veículos
              </button>
            )}
            {podeAuditarFeedback && (
              <button
                type="button"
                role="tab"
                aria-selected={modo === 'feedback'}
                className={`recepcao-tab ${modo === 'feedback' ? 'active' : ''}`}
                onClick={() => escolherAba('feedback')}
              >
                Feedback
              </button>
            )}
            {podeAuditarCotacoes && (
              <button
                type="button"
                role="tab"
                aria-selected={modo === 'cotacoes'}
                className={`recepcao-tab ${modo === 'cotacoes' ? 'active' : ''}`}
                onClick={() => escolherAba('cotacoes')}
              >
                Cotações
              </button>
            )}
            {podeAuditarCilia && (
              <button
                type="button"
                role="tab"
                aria-selected={modo === 'cilia'}
                className={`recepcao-tab ${modo === 'cilia' ? 'active' : ''}`}
                onClick={() => escolherAba('cilia')}
              >
                Orçamentos
              </button>
            )}
            {podeAuditarCotacaoOnline && (
              <button
                type="button"
                role="tab"
                aria-selected={modo === 'cotacaoOnline'}
                className={`recepcao-tab ${modo === 'cotacaoOnline' ? 'active' : ''}`}
                onClick={() => escolherAba('cotacaoOnline')}
              >
                Cotação online
              </button>
            )}
          </div>
        )}

        {modo === 'cotacaoOnline' && <AuditoriaCotacaoOnline />}

        {modo === 'cotacoes' && (
          <AuditoriaCotacoes
            cotacaoInicial={cotacaoParaAbrir}
            // Consumida no mount: sem isto, sair e voltar à aba re-selecionaria a cotação
            // antiga por cima do que o usuário tivesse escolhido depois.
            onCotacaoInicialConsumida={() => setCotacaoParaAbrir(null)}
            onAbrirVeiculo={abrirVeiculoDaPlaca}
          />
        )}

        {modo === 'cilia' && (
          <AuditoriaCilia
            orcamentoInicial={orcamentoParaAbrir}
            onOrcamentoInicialConsumido={() => setOrcamentoParaAbrir(null)}
            onAbrirVeiculo={abrirVeiculoDaPlaca}
          />
        )}

        {/*
          A parte de VEÍCULOS é escondida por CSS (`display: none`), não desmontada: manter
          o estado — veículo selecionado, filtros, página da timeline — faz alternar as abas
          voltar exatamente para onde se estava. `display: contents` quando visível para o
          wrapper não introduzir caixa nenhuma e o layout ficar idêntico ao de antes.

          ⚠️ at-90: a aba FEEDBACK compartilha este mesmo corpo, em vez de duplicar 400 linhas de
          timeline. O que muda entre as duas está condicionado a `emFeedback`: a rota consultada
          (no hook), o resumo do veículo (reduzido), a seção de comentários e os tipos de evento
          oferecidos no filtro. Duas cópias divergiriam no primeiro ajuste visual.
        */}
        <div style={{ display: modo !== 'veiculos' && modo !== 'feedback' ? 'none' : 'contents' }}>
        <div className="VisualizarOrdenar">
          <div
            className="Frame1000004037"
            style={{
              alignSelf: 'stretch',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 12,
              display: 'flex',
            }}
          >
            <div
              className="CtaBig MouseHover"
              onClick={handleOpenVehicleModal}
            >
              <div className="Frame1000003987">
                <img src={CarIcon} alt="" width="20" height="20" />
                <div className="ComponentName">Veículos</div>
              </div>
            </div>

            <div
              className="CtaBig MouseHover"
              onClick={handleOpenFiltersModal}
            >
              <div className="Frame1000003987">
                <img src={FilterIcon} alt="" width="19.8" height="19.8" />
                <div className="ComponentName">Filtros {activeFiltersCount > 0 ? `(${activeFiltersCount})` : ''}</div>
              </div>
            </div>

            <div
              className="CtaBig MouseHover"
              onClick={handleClear}
            >
              <div className="Frame1000003987">
                <img src={CleanIcon} alt="" width="20" height="20" />
                <div className="ComponentName">Limpar</div>
              </div>
            </div>
          </div>
        </div>

        <div className="QuantidadeDeCarros auditoria-summary-bar">
          <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex' }}>
            <div className="CtaQuantidadeDeCarros">
              <div className="Frame1000003987">
                <div className="ComponentName">
                  {selectedLabel
                    ? events.length < totalEvents
                      ? `${events.length} de ${totalEvents} eventos exibidos para ${selectedLabel}`
                      : `${totalEvents} eventos encontrados para ${selectedLabel}`
                    : 'Nenhum veículo selecionado'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="auditoria-layout">
          <div className="auditoria-summary-card">
            <div className="auditoria-card-heading">Resumo Atual</div>

            {!activeAudit && (
              <div className="auditoria-empty-state">
                Nenhum veículo selecionado. Por favor, selecione um veículo para visualizar a auditoria.
              </div>
            )}

            {carAuditFirstPage?.car && (
              <>
                <div className="auditoria-hero-row">
                  <div>
                    <div className="auditoria-hero-title">{carAuditFirstPage.car.plate}</div>
                    <div className="auditoria-hero-subtitle">{carAuditFirstPage.car.model}</div>
                  </div>
                  <span className="auditoria-status-badge" style={getStatusStyle(carAuditFirstPage.car.status, carAuditFirstPage.car.esteiraCor)}>{carAuditFirstPage.car.statusLabel}</span>
                </div>
                {/*
                  at-90: na aba Feedback o resumo é REDUZIDO — o backend nem devolve os outros
                  campos (ADR 0039: o setor de Eventos acompanha andamento, não proprietário nem
                  produtividade). Renderizar o grid completo aqui exibiria "undefined".
                */}
                <div className="auditoria-summary-grid">
                  {emFeedback ? (
                    <>
                      <div><strong>Entrada:</strong> {formatDateTime(carAuditFirstPage.car.entryDate)}</div>
                      <div><strong>Etapa atual:</strong> {carAuditFirstPage.car.statusLabel || '-'}</div>
                    </>
                  ) : (
                    <>
                      <div><strong>Nº do Evento:</strong> {carAuditFirstPage.car.eventNumber}</div>
                      <div><strong>Entrada:</strong> {formatDateTime(carAuditFirstPage.car.entryDate)}</div>
                      <div><strong>Colaboradores:</strong> {carAuditFirstPage.car.responsibleName || '-'}</div>
                      <div><strong>Aprovação:</strong> {carAuditFirstPage.car.approvalStatusLabel}</div>
                      <div><strong>Chassi:</strong> {carAuditFirstPage.car.chassis || '-'}</div>
                    </>
                  )}
                </div>

                {/*
                  at-90: a seção de comentários vive na aba Feedback, e é onde ela ACEITA escrita
                  — exceção declarada ao "a Auditoria é somente consulta" (dito na hint). Quem
                  escreve é decidido pela matriz, cujas permissões efetivas vêm na resposta da
                  própria seção. Na aba Veículos ela não aparece: os comentários já entram na
                  linha do tempo completa como qualquer outro evento.
                */}
                {emFeedback && <ComentariosDoVeiculo carId={carAuditFirstPage.car.id} />}

                {/*
                  Cotações de peças da placa (at-05, 14/08/2026): a cotação é movimentação
                  do veículo, mas o detalhe dela vive na aba Cotações — o clique NAVEGA
                  para lá, com a cotação já selecionada, em vez de duplicar a timeline aqui.
                  A seção sempre aparece quando há veículo escolhido: "nenhuma" registrada
                  é informação de auditoria, não ausência de seção.
                */}
                {podeAuditarCotacoes && (
                  <div className="auditoria-cotacoes-veiculo">
                    <div className="auditoria-cotacoes-veiculo-titulo">Cotações de peças</div>
                    {cotacoesDaPlaca === null ? (
                      <p className="auditoria-cotacoes-veiculo-erro" role="alert">
                        Não foi possível verificar as cotações desta placa. Recarregue a página; se
                        persistir, acione a TI.
                      </p>
                    ) : cotacoesDaPlaca.length === 0 ? (
                      <p className="auditoria-cotacoes-veiculo-vazio">
                        Nenhuma cotação registrada para esta placa.
                      </p>
                    ) : (
                      cotacoesDaPlaca.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="auditoria-cotacao-do-veiculo"
                          title="Abrir a auditoria desta cotação"
                          onClick={() => {
                            setCotacaoParaAbrir(c);
                            escolherAba('cotacoes');
                          }}
                        >
                          <span className="auditoria-cotacao-do-veiculo-numero">{c.numero}</span>
                          <span className="auditoria-cotacao-do-veiculo-status">
                            {COTACAO_STATUS_LABEL[c.status] ?? c.status}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}

                {/*
                  Orçamentos da placa (16/08/2026): mesma mecânica da seção de cotações — o
                  clique NAVEGA para a aba Orçamentos com a cadeia já selecionada, em vez de
                  duplicar a timeline aqui. Reusa as classes `auditoria-cotacoes-veiculo*`
                  de propósito: são as duas listas do mesmo card, e um CSS próprio faria
                  divergirem no primeiro ajuste visual.
                */}
                {podeAuditarCilia && (
                  <div className="auditoria-cotacoes-veiculo">
                    <div className="auditoria-cotacoes-veiculo-titulo">Orçamentos</div>
                    {orcamentosDaPlaca === null ? (
                      <p className="auditoria-cotacoes-veiculo-erro" role="alert">
                        Não foi possível verificar os orçamentos desta placa. Recarregue a página; se
                        persistir, acione a TI.
                      </p>
                    ) : orcamentosDaPlaca.length === 0 ? (
                      <p className="auditoria-cotacoes-veiculo-vazio">
                        Nenhum orçamento registrado para esta placa.
                      </p>
                    ) : (
                      orcamentosDaPlaca.map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          className="auditoria-cotacao-do-veiculo"
                          title="Abrir a auditoria deste orçamento"
                          onClick={() => {
                            setOrcamentoParaAbrir(o);
                            escolherAba('cilia');
                          }}
                        >
                          <span className="auditoria-cotacao-do-veiculo-numero">
                            {o.numeroCilia}
                            {o.qtdVersoes > 1 ? ` (${o.qtdVersoes} versões)` : ''}
                          </span>
                          <span className="auditoria-cotacao-do-veiculo-status">
                            {ESTADO_ORCAMENTO_LABEL[o.estadoAtual ?? ''] ?? o.estadoAtual ?? '—'}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="auditoria-timeline-card">
            <div className="auditoria-card-heading" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Linha do Tempo
              <button
                type="button"
                className="help-circle-btn"
                title="Como Funciona"
                onClick={() => setIsTimelineHelpOpen(true)}
              >
                ?
              </button>
            </div>

            {erroTimeline && (
              <div className="auditoria-error-state" role="alert">
                {erroTimeline}
              </div>
            )}

            {!erroTimeline && !activeAudit && (
              <div className="auditoria-empty-state">
                Por favor, selecione um veículo para visualizar a linha do tempo de auditoria.
              </div>
            )}

            {!erroTimeline && activeAudit && events.length === 0 && <div className="auditoria-empty-state">Nenhum evento encontrado para os filtros informados.</div>}

            {activeAudit && events.length > 0 && (
              <div className="auditoria-timeline-list">
                {events.map((event: AuditTimelineEvent) => (
                  <div key={event.id} className="auditoria-timeline-item">
                    <div className="auditoria-timeline-rail">
                      <TimelineIcon type={event.iconType} />
                      <div className="auditoria-timeline-line" />
                    </div>

                    {event.eventType === 'PLANEJAMENTO' ? (
                      <div className="auditoria-event-card">
                        <div className="auditoria-event-topline">
                          <span className="auditoria-event-date">
                            {formatDataHoraEvento(event.occurredAt)}
                          </span>
                          <span className="auditoria-event-type">{EVENT_TYPE_LABELS[event.eventType] || event.eventType}</span>
                        </div>

                        <div className="auditoria-event-title-row">
                          <button
                            type="button"
                            className="auditoria-event-title-link-btn"
                            onClick={() => handleOpenPlanningModal(event)}
                          >
                            Planejamento de Esteira
                          </button>
                        </div>

                        <div className="auditoria-event-description">{event.description || 'Sem descrição complementar.'}</div>

                        <div className="auditoria-event-meta-grid">
                          <div><strong>Detalhe:</strong> Clique no título para visualizar o planejamento completo.</div>
                          <div><strong>Editado por:</strong> {event.collaboratorNames.length > 0 ? event.collaboratorNames.join(', ') : 'Sistema'}</div>
                        </div>
                      </div>
                    ) : event.eventType === 'LIBERACAO' ? (
                      <div className="auditoria-event-card">
                        <div className="auditoria-event-topline">
                          <span className="auditoria-event-date">
                            {formatDataHoraEvento(event.occurredAt)}
                          </span>
                          <span className="auditoria-event-type">{EVENT_TYPE_LABELS[event.eventType] || event.eventType}</span>
                        </div>

                        <div className="auditoria-event-title-row">
                          <button
                            type="button"
                            className="auditoria-event-title-link-btn"
                            onClick={() => handleOpenLiberacaoModal(event)}
                          >
                            Liberação Administrativa
                          </button>
                        </div>

                        <div className="auditoria-event-description">{event.description || 'Sem descrição complementar.'}</div>

                        <div className="auditoria-event-meta-grid">
                          <div><strong>Detalhe:</strong> Clique no título para visualizar o detalhe da liberação.</div>
                          <div><strong>Editado por:</strong> {event.collaboratorNames.length > 0 ? event.collaboratorNames.join(', ') : 'Sistema'}</div>
                        </div>
                      </div>
                    ) : event.eventType === 'SUBTAREFA' && event.metadata?.action === 'SET_SUBTAREFAS' ? (
                      /* at-89: só a DEFINIÇÃO pelo Chefe abre modal — ela agrega N sub-tarefas em
                         várias esteiras. Concluir/reabrir uma é evento de um item só, já descrito
                         inteiro no título; modal ali seria um clique para reler a mesma frase. */
                      <div className="auditoria-event-card">
                        <div className="auditoria-event-topline">
                          <span className="auditoria-event-date">
                            {formatDataHoraEvento(event.occurredAt)}
                          </span>
                          <span className="auditoria-event-type">{EVENT_TYPE_LABELS[event.eventType] || event.eventType}</span>
                        </div>

                        <div className="auditoria-event-title-row">
                          <button
                            type="button"
                            className="auditoria-event-title-link-btn"
                            onClick={() => handleOpenSubtarefasModal(event)}
                          >
                            {event.title}
                          </button>
                        </div>

                        <div className="auditoria-event-description">{event.description || 'Sem descrição complementar.'}</div>

                        <div className="auditoria-event-meta-grid">
                          <div><strong>Detalhe:</strong> Clique no título para ver o que entrou e o que saiu, por esteira.</div>
                          <div><strong>Editado por:</strong> {event.collaboratorNames.length > 0 ? event.collaboratorNames.join(', ') : 'Sistema'}</div>
                        </div>
                      </div>
                    ) : event.eventType === 'CHECKLIST' && event.metadata?.action === 'EDIT_CHECKLIST' ? (
                      <div className="auditoria-event-card">
                        <div className="auditoria-event-topline">
                          <span className="auditoria-event-date">
                            {formatDataHoraEvento(event.occurredAt)}
                          </span>
                          <span className="auditoria-event-type">{EVENT_TYPE_LABELS[event.eventType] || event.eventType}</span>
                        </div>

                        <div className="auditoria-event-title-row">
                          <button
                            type="button"
                            className="auditoria-event-title-link-btn"
                            onClick={() => handleOpenChecklistEditModal(event)}
                          >
                            {event.title}
                          </button>
                        </div>

                        <div className="auditoria-event-description">{event.description || 'Sem descrição complementar.'}</div>

                        <div className="auditoria-event-meta-grid">
                          <div><strong>Detalhe:</strong> Clique no título para visualizar tudo o que foi editado.</div>
                          <div><strong>Editado por:</strong> {event.collaboratorNames.length > 0 ? event.collaboratorNames.join(', ') : 'Sistema'}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="auditoria-event-card">
                        <div className="auditoria-event-topline">
                          <span className="auditoria-event-date">{formatDataHoraEvento(event.occurredAt)}</span>
                          <span className="auditoria-event-type">{EVENT_TYPE_LABELS[event.eventType] || event.eventType}</span>
                        </div>

                        <div className="auditoria-event-title-row">
                          <strong>{event.title}</strong>
                          {event.statusLabel && (
                            <span className="auditoria-status-badge auditoria-status-badge--small" style={getStatusStyle(event.status || '', event.esteiraCor)}>
                              {event.statusLabel}
                            </span>
                          )}
                        </div>

                        <div className="auditoria-event-description">{event.description || 'Sem descrição complementar.'}</div>

                        <div className="auditoria-event-meta-grid">
                          {renderEventDetail(event) && (
                            <div><strong>Detalhe:</strong> {renderEventDetail(event)}</div>
                          )}
                          <div><strong>Editado por:</strong> {event.collaboratorNames.length > 0 ? event.collaboratorNames.join(', ') : 'Sistema'}</div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {carAuditQuery.hasNextPage && (
                  <button
                    type="button"
                    className="auditoria-load-more-btn"
                    onClick={() => carAuditQuery.fetchNextPage()}
                    disabled={carAuditQuery.isFetchingNextPage}
                  >
                    {carAuditQuery.isFetchingNextPage
                      ? 'Carregando…'
                      : `Carregar mais eventos (${events.length} de ${totalEvents})`}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        </div>

        {isVehicleModalOpen && (
          <div className="auditoria-modal-overlay" role="presentation" onClick={() => setIsVehicleModalOpen(false)}>
            <div className="auditoria-modal" role="dialog" aria-modal="true" aria-label="Selecionar veículo" onClick={(event) => event.stopPropagation()}>
              <div className="auditoria-modal-header">
                <h3>Selecionar veículo</h3>
                <button className="close-button" onClick={() => setIsVehicleModalOpen(false)}>&times;</button>
              </div>

              <div className="auditoria-modal-body">
                <div className="auditoria-modal-search-wrapper">
                  <img className="search-icon-inside" src={SearchIcon} alt="" width="18" height="18" />
                  <input
                    className="auditoria-modal-search"
                    type="text"
                    placeholder="Buscar por placa, modelo ou ID..."
                    value={vehicleFilterInput}
                    onChange={(event) => { setVehicleFilterInput(event.target.value); setVehiclePage(1); }}
                    aria-label="Buscar veículo"
                    autoFocus
                  />
                </div>

                <div className="auditoria-modal-count">
                  {carsSearchQuery.data?.total || 0} veículo(s) encontrado(s)
                </div>

                {carsSearchQuery.isFetching ? (
                  <div className="auditoria-modal-loading">
                    <div className="spinner"></div>
                    <p>Buscando veículos...</p>
                  </div>
                ) : (
                  <div className="auditoria-modal-list" role="listbox" aria-label="Lista de veículos">
                    {vehicleOptions.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--tema-texto-3, #6b7280)', fontFamily: 'AmpleSoft-Regular, sans-serif' }}>
                        Nenhum veículo encontrado para o filtro informado.
                      </div>
                    )}

                    {vehicleOptions.map((car) => {
                      const isSelected = car.id === selectedId;
                      return (
                        <div
                          key={car.id}
                          className={`auditoria-modal-option ${isSelected ? 'is-selected' : ''}`}
                          onClick={() => handleSelectVehicle(car.id)}
                        >
                          <div className="auditoria-modal-avatar is-vehicle">
                            <img src={getVehicleIcon(car.carClass)} alt="Carro" style={{ width: '24px', height: '24px' }} />
                          </div>
                          <div className="auditoria-modal-info">
                            <span className="auditoria-modal-name">{car.plate}</span>
                            <span className="auditoria-modal-username">{car.model}</span>
                          </div>
                          {isSelected && (
                            <div className="auditoria-modal-selected-indicator">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="auditoria-modal-pagination">
                  <div
                    className={`auditoria-modal-page-btn ${vehiclePage === 1 ? 'disabled' : ''}`}
                    onClick={() => vehiclePage > 1 && setVehiclePage(vehiclePage - 1)}
                    style={{ cursor: vehiclePage === 1 ? 'not-allowed' : 'pointer' }}
                  >
                    <img
                      className="imgIcon"
                      src={vehiclePage === 1 ? ArrowLeftGray : ArrowLeftOrange}
                      alt="Seta para Esquerda"
                      width="24"
                      height="24"
                    />
                    <div
                      className="Anterior"
                      style={{ color: vehiclePage === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)', fontSize: '14px', fontWeight: 600 }}
                    >
                      Anterior
                    </div>
                  </div>
                  <span style={{ fontSize: '13px', color: 'var(--tema-texto-2, #4b5563)', fontFamily: 'AmpleSoft-Bold, sans-serif', fontWeight: 700 }}>
                    Página {vehiclePage} de {totalVehiclePages}
                  </span>
                  <div
                    className={`auditoria-modal-page-btn ${vehiclePage >= totalVehiclePages ? 'disabled' : ''}`}
                    onClick={() => vehiclePage < totalVehiclePages && setVehiclePage(vehiclePage + 1)}
                    style={{ cursor: vehiclePage >= totalVehiclePages ? 'not-allowed' : 'pointer' }}
                  >
                    <div
                      className="PrXima"
                      style={{ color: vehiclePage >= totalVehiclePages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)', fontSize: '14px', fontWeight: 600 }}
                    >
                      Próxima
                    </div>
                    <img
                      className="imgIcon"
                      src={vehiclePage >= totalVehiclePages ? ArrowRightGray : ArrowRightOrange}
                      alt="Seta para Direita"
                      width="24"
                      height="24"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isFiltersModalOpen && (
          <div className="auditoria-modal-overlay" role="presentation" onClick={() => setIsFiltersModalOpen(false)}>
            <div className="auditoria-modal auditoria-filters-modal" role="dialog" aria-modal="true" aria-label="Filtros de Auditoria" onClick={(event) => event.stopPropagation()}>
              <div className="auditoria-modal-header">
                <h3>Filtrar Eventos</h3>
                <button type="button" className="close-button" onClick={() => setIsFiltersModalOpen(false)}>
                  &times;
                </button>
              </div>

              <div className="auditoria-filters-modal-body">
                <div className="auditoria-filter-field">
                  <label htmlFor="startDate">Data Inicial</label>
                  <input
                    id="startDate"
                    type="date"
                    value={tempFilters.startDate || ''}
                    onChange={(event) => setTempFilters((current) => ({ ...current, startDate: event.target.value }))}
                  />
                </div>

                <div className="auditoria-filter-field">
                  <label htmlFor="endDate">Data Final</label>
                  <input
                    id="endDate"
                    type="date"
                    value={tempFilters.endDate || ''}
                    onChange={(event) => setTempFilters((current) => ({ ...current, endDate: event.target.value }))}
                  />
                </div>

                <div className="auditoria-filter-field">
                  <label htmlFor="eventType">Tipo de Evento</label>
                  <select
                    id="eventType"
                    value={tempFilters.eventType || ''}
                    onChange={(event) => setTempFilters((current) => ({ ...current, eventType: event.target.value }))}
                  >
                    <option value="">Todos os eventos</option>
                    {(emFeedback ? eventTypeOptionsFeedback : eventTypeOptions).map((eventType) => (
                      <option key={eventType} value={eventType}>{EVENT_TYPE_LABELS[eventType]}</option>
                    ))}
                  </select>
                </div>

                {/*
                  Autor (at-05, 14/08/2026): veio do botão + modal próprios para cá, junto
                  dos demais critérios da timeline. A lista sai dos PRÓPRIOS eventos do
                  veículo (ver o `useMemo` de `autores`), então só oferece quem realmente
                  aparece — o `select` nasce vazio enquanto nenhum veículo foi escolhido.
                */}
                <div className="auditoria-filter-field">
                  <label htmlFor="collaboratorId">Autor</label>
                  <select
                    id="collaboratorId"
                    value={tempFilters.collaboratorId || ''}
                    onChange={(event) =>
                      setTempFilters((current) => ({ ...current, collaboratorId: event.target.value }))
                    }
                    disabled={autores.length === 0}
                  >
                    <option value="">
                      {autores.length === 0 ? 'Selecione um veículo primeiro' : 'Todos os autores'}
                    </option>
                    {autores.map((autor) => (
                      <option key={autor.id} value={autor.id}>{autor.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="auditoria-filters-modal-actions">
                <button
                  type="button"
                  className="auditoria-btn-clear"
                  onClick={() => setTempFilters({ startDate: '', endDate: '', collaboratorId: '', eventType: '' })}
                >
                  Limpar
                </button>
                <button type="button" className="auditoria-btn-apply" onClick={handleApplyFilters}>
                  Aplicar Filtros
                </button>
              </div>
            </div>
          </div>
        )}

        <PlanningComparisonModal
          isOpen={isPlanningModalOpen}
          onClose={() => setIsPlanningModalOpen(false)}
          event={selectedPlanningEvent}
        />

        <ChecklistEditDetailsModal
          isOpen={isChecklistEditModalOpen}
          onClose={() => setIsChecklistEditModalOpen(false)}
          event={selectedChecklistEditEvent}
        />

        <LiberacaoDetailsModal
          isOpen={isLiberacaoModalOpen}
          onClose={() => setIsLiberacaoModalOpen(false)}
          event={selectedLiberacaoEvent}
        />

        <SubtarefasDetailsModal
          isOpen={isSubtarefasModalOpen}
          onClose={() => setIsSubtarefasModalOpen(false)}
          event={selectedSubtarefasEvent}
        />

        <ComoFunciona
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          titulo="Como Funciona — Auditoria"
          secoes={helpSections}
        />

        <ComoFunciona
          isOpen={isTimelineHelpOpen}
          onClose={() => setIsTimelineHelpOpen(false)}
          titulo="Linha do Tempo — Tipos de evento"
          secoes={timelineHelpSections}
        />
      </div>
    </div>
  );
};

export default AuditoriaPage;